import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import graphsFa from "./HealthMonitoringGraphs-fa.json";
import graphsEn from "./HealthMonitoringGraphs-en.json";
import graphsAr from "./HealthMonitoringGraphs-ar.json";
import graphsTr from "./HealthMonitoringGraphs-tr.json";
const SleepQualityGraph: React.FC = () => {
    const { t, i18n } = useTranslation("healthMonitoring");
    const isRTL = ["fa", "ar"].includes(i18n.language);
    6;
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeTab, setActiveTab] = useState<"stages" | "timeline">("stages");

    const sleepData = [
        { time: t("graphs.sleep.times.0"), deep: 20, light: 80, rem: 0, awake: 0 },
        { time: t("graphs.sleep.times.1"), deep: 60, light: 40, rem: 0, awake: 0 },
        { time: t("graphs.sleep.times.2"), deep: 85, light: 15, rem: 0, awake: 0 },
        { time: t("graphs.sleep.times.3"), deep: 90, light: 10, rem: 0, awake: 0 },
        { time: t("graphs.sleep.times.4"), deep: 40, light: 20, rem: 40, awake: 0 },
        { time: t("graphs.sleep.times.5"), deep: 30, light: 30, rem: 40, awake: 0 },
        { time: t("graphs.sleep.times.6"), deep: 10, light: 70, rem: 20, awake: 0 },
        { time: t("graphs.sleep.times.7"), deep: 0, light: 90, rem: 0, awake: 10 },
    ];

    const deepSleepMinutes = sleepData.reduce((acc, hour) => acc + (hour.deep / 100) * 60, 0);
    const totalSleepMinutes = 8 * 60;
    const sleepQualityScore = Math.round((deepSleepMinutes / totalSleepMinutes) * 100);

    const getSleepQualityText = (score: number) => {
        if (score >= 30) return t("graphs.sleep.quality.excellent");
        if (score >= 25) return t("graphs.sleep.quality.good");
        if (score >= 20) return t("graphs.sleep.quality.average");
        return t("graphs.sleep.quality.poor");
    };

    const sleepQualityText = getSleepQualityText(sleepQualityScore);
    const sleepQualityColor = sleepQualityScore >= 30 ? "#32D74B" : sleepQualityScore >= 25 ? "#66D4CF" : sleepQualityScore >= 20 ? "#FFD60A" : "#FF9500";

    return (
        <div className="mt-4 relative" dir={isRTL ? "rtl" : "ltr"}>
            {/* Central Display */}
            <motion.div className="relative flex items-center justify-center mb-6 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <div className="relative z-10 bg-gradient-to-br from-[#002B5C]/20 to-[#002B5C]/5 rounded-full w-[120px] h-[120px] flex flex-col items-center justify-center shadow-lg border border-[#002B5C]/20">
                    <div className="text-3xl font-bold bg-gradient-to-r from-[#002B5C] to-[#002B5C]/80 bg-clip-text text-transparent">{t("graphs.sleep.totalHours")}</div>
                    <div className="text-xs text-[#002B5C]/80 font-medium">{t("graphs.sleep.timeRange")}</div>
                    <div
                        className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 bg-white/90 text-[10px] text-white px-2 py-0.5 rounded-full shadow-sm border border-[#32D74B]/10"
                        style={{ backgroundColor: sleepQualityColor }}
                    >
                        {sleepQualityText}
                    </div>
                </div>
            </motion.div>

            {/* Tabs */}
            <div className={`flex border-b border-[#ECEFF1] mb-4 flex-row`}>
                <button className={`px-4 py-2 text-sm font-medium ${activeTab === "stages" ? "text-[#002B5C] border-b-2 border-[#002B5C]" : "text-[#9EA3A9]"}`} onClick={() => setActiveTab("stages")}>
                    {t("graphs.sleep.tabs.stages")}
                </button>
                <button
                    className={`px-4 py-2 text-sm font-medium ${activeTab === "timeline" ? "text-[#002B5C] border-b-2 border-[#002B5C]" : "text-[#9EA3A9]"}`}
                    onClick={() => setActiveTab("timeline")}
                >
                    {t("graphs.sleep.tabs.timeline")}
                </button>
            </div>

            {/* Sleep Stages */}
            {activeTab === "stages" && (
                <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="bg-gradient-to-br from-[#ECEFF1]/80 to-[#ECEFF1]/30 p-2 rounded-xl text-center">
                        <div className="text-xs text-[#9EA3A9]">{t("graphs.sleep.stages.deep")}</div>
                        <div className="text-sm mt-2 font-medium text-[#0072CE]">{t("graphs.sleep.stages.deepTime")}</div>
                        <div className="text-[8px] mb-2 text-[#9EA3A9]/70 -mt-0.5">{t("graphs.sleep.stages.hours")}</div>
                        <div className="text-xs text-[#9EA3A9]">{t("graphs.sleep.stages.deepPercent")}</div>
                    </div>
                    <div className="bg-gradient-to-br from-[#ECEFF1]/80 to-[#ECEFF1]/30 p-2 rounded-xl text-center">
                        <div className="text-xs text-[#9EA3A9]">{t("graphs.sleep.stages.light")}</div>
                        <div className="text-sm mt-2 font-medium text-[#00E5FF]">{t("graphs.sleep.stages.lightTime")}</div>
                        <div className="text-[8px] mb-2 text-[#9EA3A9]/70 -mt-0.5">{t("graphs.sleep.stages.hours")}</div>
                        <div className="text-xs text-[#9EA3A9]">{t("graphs.sleep.stages.lightPercent")}</div>
                    </div>
                    <div className="bg-gradient-to-br from-[#ECEFF1]/80 to-[#ECEFF1]/30 p-2 rounded-xl text-center">
                        <div className="text-xs text-[#9EA3A9] pt-1 mt-1">REM</div>
                        <div className="text-sm mt-2 font-medium text-[#A7FFEB]">{t("graphs.sleep.stages.remTime")}</div>
                        <div className="text-[8px] mb-2 text-[#9EA3A9]/70 -mt-0.5">{t("graphs.sleep.stages.hours")}</div>
                        <div className="text-xs text-[#9EA3A9]">{t("graphs.sleep.stages.remPercent")}</div>
                    </div>
                </div>
            )}

            {/* Timeline Graph */}
            {activeTab === "timeline" && (
                <div className="mb-4">
                    <div className="h-[150px] bg-[#ECEFF1]/50 rounded-xl p-3 relative">
                        <div className={`absolute ${isRTL ? "right-2" : "left-2"} top-0 bottom-0 flex flex-col justify-between text-[10px] text-[#9EA3A9] py-2`}>
                            <span>{t("graphs.sleep.timeline.deepSleep")}</span>
                            <span>REM</span>
                            <span>{t("graphs.sleep.timeline.lightSleep")}</span>
                            <span>{t("graphs.sleep.timeline.awake")}</span>
                        </div>

                        <div className={`h-full ${isRTL ? "ml-10" : "mr-10"} flex items-end space-x-1 ${isRTL ? "space-x-reverse" : ""}`}>
                            {sleepData.map((data, i) => (
                                <div key={`bar-${i}`} className="relative flex-1 h-full flex flex-col-reverse cursor-pointer">
                                    <div className="w-full rounded-b-sm bg-[#0072CE]" style={{ height: `${data.deep}%` }} />
                                    <div className="w-full bg-[#A7FFEB]" style={{ height: `${data.rem}%` }} />
                                    <div className="w-full rounded-t-sm bg-[#00E5FF]" style={{ height: `${data.light}%` }} />
                                    {data.awake > 0 && <div className="w-full bg-[#FF9500]" style={{ height: `${data.awake}%` }} />}
                                    <div className={`absolute -bottom-5 ${isRTL ? "left-1/2" : "right-1/2"} transform ${isRTL ? "-translate-x-1/2" : "translate-x-1/2"} text-[10px] text-[#9EA3A9]`}>
                                        {data.time}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Expanded Content */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }} className="mt-4">
                        {/* Health Insights */}
                        <div className="mt-3 p-3 bg-gradient-to-r from-[#002B5C]/10 to-[#002B5C]/5 rounded-xl">
                            <div className={`text-xs font-medium text-[#002B5C] mb-1 text-start`}>{t("graphs.sleep.insights.title")}</div>
                            <div className={`text-xs text-[#4A5568] text-start`}>{t("graphs.sleep.insights.text")}</div>
                        </div>

                        {/* Sleep Efficiency Calculator */}
                        <div className="mt-3 p-3 bg-[#F5F7FA] rounded-xl">
                            <h4 className={`text-sm font-medium text-[#002B5C] mb-2 text-start`}>{t("graphs.sleep.efficiency.title")}</h4>
                            <div className={`flex items-center justify-between mb-2 flex-row`}>
                                <div className="text-xs text-[#4A5568]">{t("graphs.sleep.efficiency.timeInBed")}</div>
                                <div className="text-xs font-medium text-[#002B5C]">{t("graphs.sleep.efficiency.timeInBedValue")}</div>
                            </div>
                            <div className={`flex items-center justify-between mb-2 flex-row`}>
                                <div className="text-xs text-[#4A5568]">{t("graphs.sleep.efficiency.actualSleep")}</div>
                                <div className="text-xs font-medium text-[#002B5C]">{t("graphs.sleep.efficiency.actualSleepValue")}</div>
                            </div>
                            <div className={`flex items-center justify-between mb-2 flex-row`}>
                                <div className="text-xs text-[#4A5568]">{t("graphs.sleep.efficiency.efficiency")}</div>
                                <div className="text-xs font-medium text-[#32D74B]">{t("graphs.sleep.efficiency.efficiencyValue")}</div>
                            </div>
                            <div className="w-full h-2 bg-[#ECEFF1] rounded-full mt-1">
                                <div className="h-full bg-[#32D74B] rounded-full" style={{ width: "95.3%" }}></div>
                            </div>
                            <div className={`flex justify-between mt-1 flex-row`}>
                                <span className="text-[10px] text-[#9EA3A9]">{t("graphs.sleep.efficiency.poor")}</span>
                                <span className="text-[10px] text-[#9EA3A9]">{t("graphs.sleep.efficiency.good")}</span>
                                <span className="text-[10px] text-[#9EA3A9]">{t("graphs.sleep.efficiency.excellent")}</span>
                            </div>
                        </div>

                        {/* Weekly Trend */}
                        <div className="mt-3">
                            <div className={`text-xs text-[#9EA3A9] mb-1 text-start`}>{t("graphs.sleep.weeklyTrend.title")}</div>
                            <div className="h-24 bg-[#ECEFF1]/50 rounded-xl p-3 relative">
                                <div className="flex items-end justify-between h-full">
                                    {[41, 38, 42, 35, 40, 39, 41].map((value, index) => (
                                        <div key={index} className="flex flex-col items-center">
                                            <div className="w-4 bg-gradient-to-t from-[#002B5C] to-[#002B5C]/70 rounded-t-sm" style={{ height: `${value}%` }}></div>
                                            <div className="text-[9px] text-[#9EA3A9] mt-1">{t(`graphs.sleep.weeklyTrend.days.${index}`)}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className={`absolute top-2 ${isRTL ? "right-2" : "left-2"} bg-white/90 text-[10px] text-[#002B5C] px-2 py-1 rounded-lg shadow-sm border border-[#002B5C]/10`}>
                                    {t("graphs.sleep.weeklyTrend.badge")}
                                </div>
                            </div>
                        </div>

                        {/* Sleep Recommendations */}
                        <div className="mt-3 p-3 bg-[#F5F7FA] rounded-xl">
                            <h4 className={`text-sm font-medium text-[#002B5C] mb-1 text-start`}>{t("graphs.sleep.recommendations.title")}</h4>
                            <p className={`text-xs text-[#4A5568] text-start`}>{t("graphs.sleep.recommendations.text")}</p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SleepQualityGraph;
